interface RegionalMapping {
  [key: string]: {
    preferredLanguage: string;
    region: string;
    countries: string[];
  };
}

const REGIONAL_MAPPINGS: RegionalMapping = {
  'latin-america': {
    preferredLanguage: 'es',
    region: 'Latin America',
    countries: ['AR', 'BO', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'SV', 'GT', 'HN', 'MX', 'NI', 'PA', 'PY', 'PE', 'UY', 'VE', 'PR']
  },
  'spain': {
    preferredLanguage: 'es',
    region: 'Spain',
    countries: ['ES']
  },
  'germany': {
    preferredLanguage: 'de',
    region: 'Germany',
    countries: ['DE', 'AT', 'CH']
  },
  'netherlands': {
    preferredLanguage: 'nl',
    region: 'Netherlands',
    countries: ['NL', 'BE']
  },
  'english-speaking': {
    preferredLanguage: 'en',
    region: 'English Speaking',
    countries: ['US', 'GB', 'CA', 'AU', 'NZ', 'IE', 'ZA']
  }
};

// Latin American Spanish-speaking countries locale codes
const LATIN_AMERICAN_SPANISH_LOCALES = [
  'es-MX', 'es-AR', 'es-CO', 'es-PE', 'es-CL', 'es-VE', 'es-EC', 'es-GT',
  'es-CU', 'es-BO', 'es-DO', 'es-HN', 'es-PY', 'es-SV', 'es-NI', 'es-CR',
  'es-PA', 'es-UY', 'es-PR'
];

export interface RegionalDetectionResult {
  countryCode?: string;
  region?: string;
  preferredLanguage?: string;
  isRegionalMatch: boolean;
  confidence: number;
}

export class RegionalDetection {
  static async detectUserRegion(): Promise<RegionalDetectionResult> {
    try {
      // Try to get country from timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let countryCode = this.getCountryFromTimezone(timezone);

      // Fallback to IP geolocation (if available)
      if (!countryCode) {
        countryCode = await this.getCountryFromIP();
      }

      if (countryCode) {
        const regionalMapping = this.findRegionalMapping(countryCode);
        if (regionalMapping) {
          return {
            countryCode,
            region: regionalMapping.region,
            preferredLanguage: regionalMapping.preferredLanguage,
            isRegionalMatch: true,
            confidence: 0.8
          };
        }
      }

      return {
        isRegionalMatch: false,
        confidence: 0
      };
    } catch (error) {
      console.error('Regional detection failed:', error);
      return {
        isRegionalMatch: false,
        confidence: 0
      };
    }
  }

  // Detect Latin American Spanish from browser locale
  static detectLatinAmericanSpanish(browserLanguages: string[]): boolean {
    return browserLanguages.some(lang => 
      LATIN_AMERICAN_SPANISH_LOCALES.some(locale => 
        lang.toLowerCase().startsWith(locale.toLowerCase())
      )
    );
  }

  // Get preferred language from browser locale with regional logic
  static getPreferredLanguageFromLocale(browserLanguage: string): string {
    const normalizedLang = browserLanguage.toLowerCase();
    
    // Handle Latin American Spanish countries
    if (LATIN_AMERICAN_SPANISH_LOCALES.some(locale => 
      normalizedLang.startsWith(locale.toLowerCase())
    )) {
      return 'es'; // Map to Spanish
    }
    
    // Handle Spain Spanish specifically
    if (normalizedLang.startsWith('es-es')) {
      return 'es';
    }
    
    // Handle other Spanish variants
    if (normalizedLang.startsWith('es')) {
      return 'es';
    }
    
    // Handle Dutch (Netherlands and Belgium)
    if (normalizedLang.startsWith('nl')) {
      return 'nl';
    }
    
    // Handle German
    if (normalizedLang.startsWith('de')) {
      return 'de';
    }
    
    // Handle English (default)
    if (normalizedLang.startsWith('en')) {
      return 'en';
    }

    return 'en'; // Default to English
  }

  private static getCountryFromTimezone(timezone: string): string | null {
    // Simple timezone to country mapping
    const timezoneCountryMap: { [key: string]: string } = {
      'America/New_York': 'US',
      'America/Los_Angeles': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'America/Phoenix': 'US',
      'America/Anchorage': 'US',
      'America/Mexico_City': 'MX',
      'America/Cancun': 'MX',
      'America/Bogota': 'CO',
      'America/Buenos_Aires': 'AR',
      'America/Santiago': 'CL',
      'America/Lima': 'PE',
      'America/Caracas': 'VE',
      'America/Quito': 'EC',
      'America/La_Paz': 'BO',
      'America/Asuncion': 'PY',
      'America/Montevideo': 'UY',
      'America/Guatemala': 'GT',
      'America/San_Jose': 'CR',
      'America/Panama': 'PA',
      'America/Tegucigalpa': 'HN',
      'America/Managua': 'NI',
      'America/San_Salvador': 'SV',
      'America/Santo_Domingo': 'DO',
      'America/Havana': 'CU',
      'America/Puerto_Rico': 'PR',
      'Europe/Berlin': 'DE',
      'Europe/Vienna': 'AT',
      'Europe/Zurich': 'CH',
      'Europe/Madrid': 'ES',
      'Europe/London': 'GB',
      'Europe/Amsterdam': 'NL',
      'Europe/Brussels': 'BE',
      'America/Toronto': 'CA',
      'America/Vancouver': 'CA',
      'Australia/Sydney': 'AU',
      'Australia/Melbourne': 'AU',
      'Pacific/Auckland': 'NZ',
      'Africa/Johannesburg': 'ZA'
    };

    return timezoneCountryMap[timezone] || null;
  }

  private static async getCountryFromIP(): Promise<string | null> {
    try {
      // Use a free IP geolocation service
      const response = await fetch('https://ipapi.co/country/', {
        timeout: 5000
      } as any);
      const countryCode = await response.text();
      return countryCode.trim().toUpperCase();
    } catch {
      return null;
    }
  }

  private static findRegionalMapping(countryCode: string) {
    for (const [key, mapping] of Object.entries(REGIONAL_MAPPINGS)) {
      if (mapping.countries.includes(countryCode)) {
        return mapping;
      }
    }
    return null;
  }
}

export default RegionalDetection;